import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { RoundedBox, Html } from "@react-three/drei";
import { scrollStore } from "@/lib/scrollStore";
import { debugState } from "@/lib/webgl";

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const seg = (p, a, b) => clamp01((p - a) / (b - a));
const ease = (t) => t * t * (3 - 2 * t);

function makeFrontTexture() {
    const c = document.createElement("canvas");
    c.width = 512;
    c.height = 820;
    const x = c.getContext("2d");
    x.fillStyle = "#ffffff";
    x.fillRect(0, 0, 512, 820);
    x.textAlign = "center";
    x.fillStyle = "#4d9430";
    x.font = "italic 700 62px Manrope, Arial, sans-serif";
    x.fillText("Oral Fluid", 256, 150);
    x.font = "italic 800 60px Manrope, Arial, sans-serif";
    x.fillText("Drug Test Cube", 256, 228);
    x.font = "600 21px Manrope, Arial, sans-serif";
    x.fillStyle = "#4d9430";
    x.fillText("Desprenda de derecha a izquierda", 256, 505);
    x.strokeStyle = "#4d9430";
    x.lineWidth = 3;
    x.beginPath();
    x.moveTo(150, 540);
    x.lineTo(362, 540);
    x.stroke();
    x.beginPath();
    x.moveTo(150, 540);
    x.lineTo(168, 531);
    x.lineTo(168, 549);
    x.closePath();
    x.fillStyle = "#4d9430";
    x.fill();
    x.textAlign = "left";
    x.font = "600 24px Manrope, Arial, sans-serif";
    x.fillStyle = "#334155";
    x.fillText("ID:", 70, 610);
    x.fillText("Fecha:", 70, 665);
    x.strokeStyle = "#94a3b8";
    x.lineWidth = 2;
    x.beginPath();
    x.moveTo(120, 615);
    x.lineTo(430, 615);
    x.moveTo(150, 670);
    x.lineTo(430, 670);
    x.stroke();
    const icons = [
        { label: "NEGATIVO", cx: 118, cLine: true, tLine: true },
        { label: "POSITIVO", cx: 256, cLine: true, tLine: false },
        { label: "INVÁLIDO", cx: 394, cLine: false, tLine: true },
    ];
    icons.forEach((ic) => {
        x.strokeStyle = "#475569";
        x.lineWidth = 2;
        x.strokeRect(ic.cx - 34, 705, 28, 44);
        x.strokeRect(ic.cx + 6, 705, 28, 44);
        x.font = "700 15px Manrope, Arial, sans-serif";
        x.fillStyle = "#475569";
        x.textAlign = "center";
        x.fillText("C", ic.cx - 20, 700);
        x.fillText("T", ic.cx + 20, 700);
        if (ic.cLine) {
            x.fillStyle = "#dc2626";
            x.fillRect(ic.cx - 27, 722, 14, 4);
        }
        if (ic.tLine) {
            x.fillStyle = "#dc2626";
            x.fillRect(ic.cx + 13, 722, 14, 4);
        }
        x.fillStyle = "#334155";
        x.font = "600 17px Manrope, Arial, sans-serif";
        x.fillText(ic.label, ic.cx, 785);
    });
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 4;
    return tex;
}

function makeBandTexture() {
    const c = document.createElement("canvas");
    c.width = 1024;
    c.height = 150;
    const x = c.getContext("2d");
    x.fillStyle = "#4e9a2f";
    x.fillRect(0, 0, 1024, 150);
    x.textAlign = "center";
    x.fillStyle = "#ffffff";
    x.font = "800 46px Manrope, Arial, sans-serif";
    x.fillText("AUTHORIZED PERSONNEL ONLY", 560, 62);
    x.font = "italic 700 32px Manrope, Arial, sans-serif";
    x.fillText("Remove Label for Results", 560, 112);
    x.strokeStyle = "#ffffff";
    x.lineWidth = 6;
    x.beginPath();
    x.arc(90, 75, 42, 0, Math.PI * 2);
    x.stroke();
    x.font = "800 44px Manrope, Arial, sans-serif";
    x.fillText("!", 90, 92);
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 4;
    return tex;
}

function PartLabel({ position, text, register }) {
    return (
        <Html position={position} center zIndexRange={[5, 0]} style={{ pointerEvents: "none" }}>
            <div ref={register} className="part-label" style={{ opacity: 0 }}>
                <span className="part-label-dot" />
                <span className="part-label-line" />
                <span className="part-label-text">{text}</span>
            </div>
        </Html>
    );
}

const STRIP_COLORS = ["#1f2937", "#16a34a", "#06b6d4", "#2563eb", "#7c3aed"];

let heroWindowEl = null;

export function LyherCube({ mode = "scroll" }) {
    const root = useRef();
    const inner = useRef();
    const capRef = useRef();
    const colRef = useRef();
    const frontRef = useRef();
    const backRef = useRef();
    const bandRef = useRef();
    const stripsRef = useRef();
    const satMat = useRef();
    const labelEls = useRef([]);

    const frontTex = useMemo(makeFrontTexture, []);
    const bandTex = useMemo(makeBandTexture, []);
    const white = useMemo(() => new THREE.Color("#ffffff"), []);
    const red = useMemo(() => new THREE.Color("#ef4444"), []);

    const register = (el) => {
        if (el && !labelEls.current.includes(el)) labelEls.current.push(el);
    };

    const plastic = useMemo(
        () => ({ color: "#f8fafc", roughness: 0.32, metalness: 0.05 }),
        []
    );

    useFrame((state) => {
        const t = state.clock.elapsedTime;
        const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
        const vp = state.viewport;
        const fitScale = Math.min((vp.height * 0.56) / 4.6, (vp.width * 0.72) / 1.9);
        const heroScaleM = Math.min((vp.width * 0.68) / 1.9, (vp.height * 0.8) / 4.4);
        if (mode === "float") {
            if (root.current) {
                root.current.position.set(0, -0.45 + Math.sin(t * 0.8) * 0.12, 0);
                root.current.scale.setScalar(isMobile ? Math.min(fitScale, 0.9) : 0.92);
            }
            if (inner.current) {
                inner.current.rotation.y = t * 0.35;
                inner.current.rotation.x = 0.05;
            }
            return;
        }

        const h = scrollStore.hero;
        const p = scrollStore.exp;
        if (h >= 1 && p >= 1) return;
        const s1 = ease(seg(p, 0.0, 0.24));
        const eIn = ease(seg(p, 0.26, 0.38));
        const eOut = ease(seg(p, 0.42, 0.52));
        const e = eIn - eOut;
        const zsat = ease(seg(p, 0.54, 0.62)) - ease(seg(p, 0.68, 0.76));
        const zr = ease(seg(p, 0.78, 0.85)) - ease(seg(p, 0.94, 1.0));

        let rotY = h * 0.7 + s1 * Math.PI * 2.15 + Math.sin(t * 0.15) * 0.05;
        const nearest = Math.round(rotY / (Math.PI * 2)) * Math.PI * 2;
        rotY = rotY + (nearest - rotY) * zr;
        if (inner.current) {
            inner.current.rotation.y = rotY;
            inner.current.rotation.x = 0.04 * Math.sin(t * 0.2);
        }

        if (isMobile && h < 1 && !heroWindowEl && typeof document !== "undefined") {
            heroWindowEl = document.querySelector('[data-testid="hero-product-window"]');
        }
        let yHeroM = -0.33 * vp.height;
        if (isMobile && h < 1 && heroWindowEl) {
            const r = heroWindowEl.getBoundingClientRect();
            const frac = (r.top + r.height / 2) / (window.innerHeight || 1);
            yHeroM = (0.5 - frac) * vp.height;
        }
        const enterM = isMobile && h >= 1 ? (1 - ease(seg(p, 0.0, 0.07))) * 3.5 : 0;
        const base = isMobile ? (h < 1 ? heroScaleM : Math.min(fitScale, 1.0)) : 1.0;
        const zoomK = isMobile ? 0.6 : 1;
        const heroX = isMobile ? 0 : 1.9 * (1 - h);
        const x = heroX + (isMobile ? 0 : Math.min(1.15, vp.width * 0.18)) * zsat;
        const anchorY = isMobile ? (h < 1 ? yHeroM : -1.12 - enterM) : -0.15;
        const y =
            anchorY +
            Math.sin(t * 0.9) * 0.07 * (1 - h * 0.5) -
            0.5 * e -
            (isMobile ? 2.3 * base - 0.55 : 2.0) * zsat +
            (isMobile ? 1.15 * base : 1.1) * zr;
        const s = base * (1 + 0.22 * Math.sin(s1 * Math.PI) + 0.95 * zoomK * zsat + 0.7 * zoomK * zr);
        if (root.current) {
            root.current.position.set(x, y, 0);
            root.current.scale.setScalar(s);
            debugState.modelScale = s;
        }
        if (capRef.current) capRef.current.position.y = 1.62 + 0.95 * e;
        if (colRef.current) colRef.current.position.y = 1.7 * e;
        if (frontRef.current) frontRef.current.position.z = 1.15 * e;
        if (backRef.current) backRef.current.position.z = -0.7 * e;
        if (bandRef.current) {
            bandRef.current.position.x = (isMobile ? 1.15 : 1.95) * e;
            bandRef.current.rotation.z = 0.15 * e;
        }
        if (stripsRef.current) {
            stripsRef.current.position.z = 0.6 * e;
            stripsRef.current.children.forEach((m, i) => {
                m.position.x = (i - 2) * 0.22 + (i - 2) * 0.18 * e;
            });
        }
        if (satMat.current) {
            const sat = ease(seg(p, 0.56, 0.66));
            satMat.current.color.lerpColors(white, red, sat);
        }
        const lo = clamp01((e - 0.3) / 0.25);
        labelEls.current.forEach((el) => {
            if (el) el.style.opacity = lo;
        });
    });

    return (
        <group ref={root}>
            <group ref={inner} position={[0, -0.55, 0]}>
                {/* Back shell */}
                <group ref={backRef}>
                    <RoundedBox args={[1.7, 2.7, 0.95]} radius={0.09} smoothness={4} position={[0, 0, -0.06]}>
                        <meshStandardMaterial {...plastic} />
                    </RoundedBox>
                    <PartLabel position={[-1.55, 0.7, -0.3]} text="Cube" register={register} />
                </group>

                {/* Test strips */}
                <group ref={stripsRef}>
                    {STRIP_COLORS.map((col, i) => (
                        <group key={col} position={[(i - 2) * 0.22, 0.1, 0.16]}>
                            <RoundedBox args={[0.15, 1.8, 0.05]} radius={0.02}>
                                <meshStandardMaterial color="#ffffff" roughness={0.5} />
                            </RoundedBox>
                            <mesh position={[0, 0.82, 0]}>
                                <boxGeometry args={[0.15, 0.22, 0.055]} />
                                <meshStandardMaterial color={col} roughness={0.4} />
                            </mesh>
                        </group>
                    ))}
                    <PartLabel position={[1.7, 0.5, 0.3]} text="Tiras de análisis" register={register} />
                </group>

                {/* Front shell with printed panel */}
                <group ref={frontRef}>
                    <RoundedBox args={[1.7, 2.7, 0.28]} radius={0.08} smoothness={4} position={[0, 0, 0.37]}>
                        <meshStandardMaterial {...plastic} />
                    </RoundedBox>
                    <mesh position={[0, 0, 0.515]}>
                        <planeGeometry args={[1.56, 2.5]} />
                        <meshStandardMaterial map={frontTex} roughness={0.5} />
                    </mesh>
                    <PartLabel position={[1.75, -0.95, 0.4]} text="Zona de resultados" register={register} />
                </group>

                {/* Security band */}
                <group ref={bandRef}>
                    <mesh position={[0, -0.48, 0]}>
                        <boxGeometry args={[1.78, 0.38, 1.0]} />
                        <meshStandardMaterial color="#4e9a2f" roughness={0.45} />
                    </mesh>
                    <mesh position={[0, -0.48, 0.505]}>
                        <planeGeometry args={[1.7, 0.34]} />
                        <meshStandardMaterial map={bandTex} roughness={0.5} />
                    </mesh>
                    <PartLabel position={[1.6, 0.35, 0.4]} text="Etiqueta de seguridad" register={register} />
                </group>

                {/* Ribbed cap */}
                <group ref={capRef} position={[0, 1.62, 0]}>
                    {[0.64, 0.69, 0.64, 0.69, 0.64].map((r, i) => (
                        <mesh key={i} position={[0, -0.18 + i * 0.09, 0]}>
                            <cylinderGeometry args={[r, r, 0.085, 48]} />
                            <meshStandardMaterial color="#f1f5f9" roughness={0.45} />
                        </mesh>
                    ))}
                    <mesh position={[0, 0.22, 0]}>
                        <cylinderGeometry args={[0.71, 0.71, 0.07, 48]} />
                        <meshStandardMaterial color="#f8fafc" roughness={0.4} />
                    </mesh>
                </group>

                {/* Collector: locking handle, stem, indicator window, saturation strip, swab */}
                <group ref={colRef}>
                    <group position={[0, 2.78, 0]}>
                        {[0.52, 0.58, 0.52, 0.58, 0.52, 0.58].map((r, i) => (
                            <mesh key={i} position={[0, -0.18 + i * 0.075, 0]}>
                                <cylinderGeometry args={[r, r, 0.07, 48]} />
                                <meshStandardMaterial color="#f1f5f9" roughness={0.45} />
                            </mesh>
                        ))}
                        <mesh position={[0, 0.24, 0]}>
                            <cylinderGeometry args={[0.6, 0.6, 0.06, 48]} />
                            <meshStandardMaterial color="#f8fafc" roughness={0.4} />
                        </mesh>
                        <PartLabel position={[1.35, 0.15, 0]} text="Mango de bloqueo" register={register} />
                    </group>
                    <mesh position={[0, 2.2, 0]}>
                        <cylinderGeometry args={[0.065, 0.065, 1.0, 24]} />
                        <meshStandardMaterial color="#e2e8f0" roughness={0.3} transparent opacity={0.95} />
                    </mesh>
                    <PartLabel position={[-1.15, 2.2, 0]} text="Colector" register={register} />
                    <RoundedBox args={[0.17, 0.24, 0.05]} radius={0.02} position={[0, 2.44, 0.07]}>
                        <meshStandardMaterial color="#ffffff" roughness={0.4} />
                    </RoundedBox>
                    <PartLabel position={[1.15, 2.44, 0.1]} text="Ventana indicadora" register={register} />
                    <mesh position={[0, 2.02, 0.07]}>
                        <boxGeometry args={[0.08, 0.42, 0.03]} />
                        <meshStandardMaterial ref={satMat} color="#ffffff" roughness={0.4} />
                    </mesh>
                    <PartLabel position={[-1.35, 2.0, 0.1]} text="Indicador de saturación" register={register} />
                    <group position={[0, 1.6, 0]}>
                        <mesh>
                            <cylinderGeometry args={[0.16, 0.16, 0.34, 24]} />
                            <meshStandardMaterial color="#f1f5f9" roughness={0.7} />
                        </mesh>
                        <mesh position={[0, 0.17, 0]}>
                            <sphereGeometry args={[0.16, 24, 16]} />
                            <meshStandardMaterial color="#f1f5f9" roughness={0.7} />
                        </mesh>
                        <mesh position={[0, -0.17, 0]}>
                            <sphereGeometry args={[0.16, 24, 16]} />
                            <meshStandardMaterial color="#f1f5f9" roughness={0.7} />
                        </mesh>
                        <PartLabel position={[1.1, -0.05, 0]} text="Hisopo" register={register} />
                    </group>
                </group>
            </group>
        </group>
    );
}
